
import React, { useEffect, useState } from "react";
import { usePlayerPayments } from "@/hooks/usePlayerPayments";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Loader2, RefreshCw, Video, FileText, TrendingUp, Calendar } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/player/EmptyState";
import { Button } from "@/components/ui/button";
import { useQueryClient } from "@tanstack/react-query";

const Pagamentos = () => {
  const { payments, loading } = usePlayerPayments();
  const [filterType, setFilterType] = useState<'all' | 'mentorship' | 'digital_content'>('all');

  useEffect(() => {
    console.log("💳 [Pagamentos] Componente atualizado com pagamentos:", {
      total: payments.length,
      mentorias: payments.filter(p => p.type === 'mentorship').length,
      conteudos: payments.filter(p => p.type === 'digital_content').length
    });
  }, [payments]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 text-[#00B6F0] animate-spin" />
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', { 
      timeZone: 'America/Sao_Paulo',
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  const formatCurrency = (cents: number) => {
    return (cents / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  const getStatusBadge = (status: string) => {
    switch(status.toLowerCase()) {
      case 'completed':
      case 'success':
      case 'paid':
        return <Badge className="bg-green-600">Aprovado</Badge>;
      case 'pending':
      case 'awaiting':
      case 'awaiting_payment':
        return <Badge className="bg-yellow-600">Pendente</Badge>;
      case 'failed':
      case 'error':
        return <Badge className="bg-red-600">Falhou</Badge>;
      case 'refunded':
        return <Badge className="bg-blue-600">Reembolsado</Badge>;
      default:
        return <Badge className="bg-gray-500">{status}</Badge>;
    }
  };

  const getServiceIcon = (type: 'mentorship' | 'digital_content') => {
    return type === 'mentorship' ? (
      <Video className="h-4 w-4 text-[#00B6F0]" />
    ) : (
      <FileText className="h-4 w-4 text-purple-400" />
    );
  };

  const filteredPayments = payments.filter(payment => {
    if (filterType === 'all') return true;
    return payment.type === filterType;
  });

  const stats = {
    total: payments.length,
    mentorships: payments.filter(p => p.type === 'mentorship').length,
    digitalContents: payments.filter(p => p.type === 'digital_content').length,
    totalValue: payments.reduce((sum, p) => sum + p.amount_cents, 0)
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Histórico de Pagamentos</h1>
          <p className="text-gray-400 mt-1">
            Visualize todos os seus pagamentos de mentorias e conteúdos digitais
          </p>
        </div>
        <Button 
          variant="outline" 
          size="icon"
          onClick={handleRefresh}
          className="h-9 w-9"
          title="Atualizar pagamentos"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Stats Cards */}
      {payments.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-[#1A2233] border-[#222A35] text-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#00B6F0]/20 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-[#00B6F0]" />
                </div>
                <div>
                  <p className="text-sm text-gray-400">Total de Pagamentos</p>
                  <p className="text-xl font-bold">{stats.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1A2233] border-[#222A35] text-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#00B6F0]/20 rounded-lg">
                  <Video className="h-5 w-5 text-[#00B6F0]" />
                </div>
                <div>
                  <p className="text-sm text-gray-400">Mentorias</p>
                  <p className="text-xl font-bold">{stats.mentorships}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1A2233] border-[#222A35] text-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <FileText className="h-5 w-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-400">Conteúdos Digitais</p>
                  <p className="text-xl font-bold">{stats.digitalContents}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1A2233] border-[#222A35] text-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <Calendar className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-gray-400">Valor Total</p>
                  <p className="text-xl font-bold">{formatCurrency(stats.totalValue)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      {payments.length > 0 && (
        <div className="flex gap-2 mb-6">
          <Button 
            variant={filterType === 'all' ? 'default' : 'outline'}
            onClick={() => setFilterType('all')}
            className={filterType === 'all' ? 'bg-[#00B6F0] text-black' : 'border-[#222A35] text-gray-400'}
          >
            Todos ({stats.total})
          </Button>
          <Button 
            variant={filterType === 'mentorship' ? 'default' : 'outline'}
            onClick={() => setFilterType('mentorship')}
            className={filterType === 'mentorship' ? 'bg-[#00B6F0] text-black' : 'border-[#222A35] text-gray-400'}
          >
            <Video className="h-4 w-4 mr-2" />
            Mentorias ({stats.mentorships})
          </Button>
          <Button 
            variant={filterType === 'digital_content' ? 'default' : 'outline'}
            onClick={() => setFilterType('digital_content')}
            className={filterType === 'digital_content' ? 'bg-[#00B6F0] text-black' : 'border-[#222A35] text-gray-400'}
          >
            <FileText className="h-4 w-4 mr-2" />
            Conteúdos ({stats.digitalContents})
          </Button>
        </div>
      )}

      {filteredPayments.length === 0 ? (
        <EmptyState 
          title="Nenhum pagamento encontrado" 
          description="Você ainda não realizou nenhum pagamento."
          actionLabel="Explorar Mentorias"
          actionHref="/mentorias"
        />
      ) : (
        <Card className="bg-[#1A2233] border-[#222A35] text-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Histórico de Pagamentos
              <Badge variant="secondary" className="bg-[#00B6F0]/20 text-[#00B6F0]">
                {filteredPayments.length} item{filteredPayments.length !== 1 ? 's' : ''}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[#222A35] hover:bg-transparent">
                  <TableHead className="text-[#00B6F0]">Tipo</TableHead>
                  <TableHead className="text-[#00B6F0]">Data</TableHead>
                  <TableHead className="text-[#00B6F0]">Serviço</TableHead>
                  <TableHead className="text-[#00B6F0]">Mentor</TableHead>
                  <TableHead className="text-[#00B6F0]">Valor</TableHead>
                  <TableHead className="text-[#00B6F0]">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPayments.map((payment) => (
                  <TableRow key={payment.id} className="border-b border-[#222A35]">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getServiceIcon(payment.type)}
                        <span className="text-xs text-gray-400">
                          {payment.type === 'mentorship' ? 'Mentoria' : 'Conteúdo'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(payment.created_at)}</TableCell>
                    <TableCell className="max-w-[200px] truncate" title={payment.service_title}>
                      {payment.service_title}
                    </TableCell>
                    <TableCell>{payment.mentor_name}</TableCell>
                    <TableCell>{formatCurrency(payment.amount_cents)}</TableCell>
                    <TableCell>{getStatusBadge(payment.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Pagamentos;
